import axios from "axios";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Validation schema using Yup
const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  state: Yup.string()
    .min(2, "State must be at least 2 characters")
    .required("State is required"),
  district: Yup.string()
    .min(2, "District must be at least 2 characters")
    .required("District is required"),
  city: Yup.string()
    .min(2, "City must be at least 2 characters")
    .required("City is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone Number is required"),
  message: Yup.string()
    .min(5, "Message must be at least 5 characters")
    .required("Message is required"),
});

const Contact = () => {
  const initialValues = {
    name: "",
    email: "",
    phone: "",
    state: "",
    district: "",
    city: "",
    message: "",
  };

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setStatus },
  ) => {
    try {
      const response = await axios.post(
        "https://lifeonplus.in/api/contactForm",
        values,
      );
      toast.success("Form submitted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setStatus({ success: "Form submitted successfully!" });
      resetForm();
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({ error: "Failed to submit form. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Banner Image */}
      <div className="w-screen h-64 md:h-[430px] border-[3px] border-[#35A93E]">
        <img
          src="/contactBanner.jpeg"
          alt="Contact Banner"
          className="w-full h-full object-contain"
        />
      </div>

      <div className="max-w-6xl w-full space-y-20 pt-12 pb-12">
        {/* Header Section */}
        <div className="text-center animate-slide-in">
          <h1 className="text-xl md:text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#35A93E] to-[#2E8C36] tracking-tight leading-tight">
            We’re Here to Help You — Anytime, Anywhere
          </h1>
          <p className="mt-2 text-sm text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Whether you have a question, feedback, partnership proposal, or need
            support, our team is just a message away. Reach out to us — and
            let’s build a healthier future together.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Information Section */}
          <div className="lg:col-span-2 bg-gradient-to-br from-white to-[#F5FFF2] rounded-3xl shadow-2xl p-8 transform hover:-translate-y-3 transition-all duration-500">
            <h2 className="text-xl font-bold text-[#35A93E] mb-10 animate-fade-in">
              Get in Touch
            </h2>
            <div className="space-y-8">
              <div className="flex items-start space-x-5 animate-slide-up delay-200">
                <svg
                  className="w-7 h-7 text-[#35A93E] mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Whatsapp
                  </h3>
                  <p className="text-gray-600 mt-2">99868 80000</p>
                </div>
              </div>
              <div className="flex items-start space-x-5 animate-slide-up delay-300">
                <svg
                  className="w-7 h-7 text-[#35A93E] mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                  <p className="text-gray-600">
                    <a
                      href="mailto:info@lifeonplus.com"
                      className="text-[#35A93E] hover:text-[#2E8C36] transition-colors"
                    >
                      info@lifeonplus.com
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-5 animate-slide-up delay-400">
                <svg
                  className="w-7 h-7 text-[#35A93E] mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m0 18a9 9 0 01-9-9m9 9v-18m-9 0V6m12 6H3m18 0h-6m-6 0H3"
                  />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Website
                  </h3>
                  <p className="text-gray-600 mt-2">
                    <a
                      href="http://www.lifeonplus.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#35A93E] hover:text-[#2E8C36] transition-colors"
                    >
                      www.lifeonplus.com
                    </a>{" "}
                    |
                    <a
                      href="http://www.lifeonplus.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#35A93E] hover:text-[#2E8C36] transition-colors"
                    >
                      www.lifeonplus.in
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-5 animate-slide-up delay-500">
                <svg
                  className="w-16 h-16 text-[#35A93E]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M18 9v3m0 0v3m0-6h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    App Support
                  </h3>
                  <p className="text-gray-600 mt-2">
                    For app-related queries, use the in-app help section or
                    contact our support team.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="lg:col-span-3 bg-white rounded-3xl shadow-2xl p-10 transform hover:-translate-y-3 transition-all duration-500">
            <h2 className="text-xl font-bold text-[#35A93E] mb-3 animate-fade-in">
              Send Us a Message
            </h2>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ status, isSubmitting }) => (
                <Form className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        placeholder="Enter your full name"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Enter your email address"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Phone
                      </label>
                      <Field
                        type="tel"
                        name="phone"
                        id="phone"
                        placeholder="Enter your phone number"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="state"
                        className="block text-sm font-medium text-gray-700"
                      >
                        State
                      </label>
                      <Field
                        type="text"
                        name="state"
                        id="state"
                        placeholder="Enter your state"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="state"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="district"
                        className="block text-sm font-medium text-gray-700"
                      >
                        District
                      </label>
                      <Field
                        type="text"
                        name="district"
                        id="district"
                        placeholder="Enter your district"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="district"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="city"
                        className="block text-sm font-medium text-gray-700"
                      >
                        City
                      </label>

                      <Field
                        type="text"
                        name="city"
                        id="city"
                        placeholder="Enter your city"
                        className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                      />
                      <ErrorMessage
                        name="city"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Interested In
                    </label>
                    <Field
                      as="textarea"
                      name="message"
                      id="message"
                      rows="3"
                      placeholder="Enter your interested product or message"
                      className="mt-2 block w-full border border-[#E8F7E9] rounded-xl shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#35A93E] focus:border-[#35A93E] sm:text-sm transition-all duration-200"
                    />
                    <ErrorMessage
                      name="message"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-[#35A93E] hover:bg-[#2E8C36] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#35A93E] transition-all duration-200 disabled:bg-gray-400"
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                  {status && status.success && (
                    <div className="text-green-600 text-sm text-center">
                      {status.success}
                    </div>
                  )}
                  {status && status.error && (
                    <div className="text-red-600 text-sm text-center">
                      {status.error}
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Custom CSS for Animations */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in {
          animation: slideIn 0.9s ease-out forwards;
        }
        .animate-fade-in {
          animation: fadeIn 1.2s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.7s ease-out forwards;
        }
        .delay-100 {
          animation-delay: 0.1s;
        }
        .delay-200 {
          animation-delay: 0.2s;
        }
        .delay-300 {
          animation-delay: 0.3s;
        }
        .delay-400 {
          animation-delay: 0.4s;
        }
        .delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </div>
  );
};

export default Contact;
